/**
 * VistoeraTASK-00589 — Overview「概览」Tab: profile, plan, metrics, charts.
 * No large project/asset lists; no team management cards.
 */
import { MessageOutlined, PictureOutlined, VideoCameraOutlined, WalletOutlined } from '@ant-design/icons'
import { Alert, Button, Empty, Segmented, Skeleton, Tag } from 'antd'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { getOverviewUsageTrend } from '../../api'
import type { TenantOverview } from '../../types'
import TrendLineChart from './TrendLineChart'

function initialsFrom(name: string | undefined, email: string | undefined): string {
  const source = (name || '').trim() || (email || '').trim()
  if (!source) return '?'
  return source.slice(0, 1).toUpperCase()
}

function roleBadgeLabel(
  t: (k: string) => string,
  identity: TenantOverview['identity'],
): string {
  const label = identity?.role_label
  if (label === 'team_admin') return t('overview:tabs.v3.badge.teamAdmin')
  if (label === 'team_member' || identity?.workspace_kind === 'team') return t('overview:tabs.v3.badge.teamMember')
  return t('overview:tabs.v3.badge.personal')
}

export default function OverviewHomeTab({
  overview,
  workspaceId,
  onEditProfile,
  onManagePlan,
  onOpenCredits,
}: {
  overview: TenantOverview
  workspaceId: string
  onEditProfile: () => void
  onManagePlan: () => void
  onOpenCredits: () => void
}) {
  const { t } = useTranslation(['overview'])
  const identity = overview.identity
  const plan = overview.plan
  const usage = overview.period_usage
  const displayName = identity?.user.display_name || '—'
  const email = identity?.user.email || '—'
  const planName = plan.plan_display_name || plan.label
  const [days, setDays] = useState<7 | 14 | 30>(30)
  const [trendLoading, setTrendLoading] = useState(false)
  const [trendError, setTrendError] = useState<string | null>(null)
  const [trendPoints, setTrendPoints] = useState<Array<{ date: string; value: number }>>([])
  const [distribution, setDistribution] = useState<Array<{ key: string; count: number; credits: string }>>([])

  useEffect(() => {
    let cancelled = false
    setTrendLoading(true)
    setTrendError(null)
    getOverviewUsageTrend(workspaceId, days)
      .then((r) => {
        if (cancelled) return
        setTrendPoints(
          (r.data.points || []).map((p) => ({
            date: p.date,
            value: Number(p.credits) || 0,
          })),
        )
        setDistribution(r.data.distribution || [])
      })
      .catch((err) => {
        if (cancelled) return
        setTrendPoints([])
        setDistribution([])
        setTrendError(err?.message || t('overview:tabs.v3.home.trendFailed'))
      })
      .finally(() => {
        if (!cancelled) setTrendLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [workspaceId, days, t])

  const distTotal = useMemo(() => distribution.reduce((s, d) => s + (d.count || 0), 0), [distribution])

  return (
    <div className="tc-ov-v3-home" data-testid="overview-home-tab">
      <div className="tc-ov-v3-top-row">
        <div className="tc-data-card tc-ov-v3-card" data-testid="overview-profile-card">
          <div className="tc-ov-v3-profile-head">
            <div className="tc-ov-v2-avatar">{initialsFrom(displayName, email)}</div>
            <div className="tc-ov-v2-profile-meta">
              <div className="tc-ov-v2-profile-name">{displayName}</div>
              <div className="tc-ov-v2-profile-email">{email}</div>
              <Tag color={identity?.workspace_kind === 'team' ? 'purple' : 'blue'}>{roleBadgeLabel(t, identity)}</Tag>
            </div>
            <Button size="small" onClick={onEditProfile} data-testid="overview-edit-profile">
              {t('overview:tabs.v3.home.editProfile')}
            </Button>
          </div>
        </div>
        <div className="tc-data-card tc-ov-v3-card" data-testid="overview-plan-card">
          <div className="tc-ov-v2-plan-label">{t('overview:tabs.v3.home.currentPlan')}</div>
          <div className="tc-ov-v2-plan-name">{planName}</div>
          <div className="tc-ov-v2-plan-meta">
            <Tag color={plan.status === 'active' ? 'green' : 'default'}>{plan.status}</Tag>
          </div>
          <Button type="primary" size="small" onClick={onManagePlan} data-testid="plan-upgrade-cta">
            {t('overview:tabs.v3.home.managePlan')}
          </Button>
        </div>
      </div>

      <div className="tc-ov-v3-metrics-row" data-testid="overview-metrics-row">
        <div className="tc-data-card tc-ov-v3-metric">
          <div className="tc-ov-v3-metric-head">
            <WalletOutlined /> {t('overview:tabs.v3.home.creditsBalance')}
          </div>
          <div className="tc-ov-v3-metric-value">{usage?.credit_balance ?? plan.credit_balance}</div>
          <div className="tc-ov-v3-metric-sub">
            {t('overview:tabs.v3.home.creditsUsedMonth', { used: usage?.credits_used ?? '—' })}
          </div>
          <Button type="link" size="small" onClick={onOpenCredits}>
            {t('overview:tabs.v3.home.viewCredits')}
          </Button>
        </div>
        <div className="tc-data-card tc-ov-v3-metric">
          <div className="tc-ov-v3-metric-head">
            <PictureOutlined /> {t('overview:tabs.v3.home.images')}
          </div>
          <div className="tc-ov-v3-metric-value">{usage?.images.used ?? '—'}</div>
          <div className="tc-ov-v3-metric-sub">{t('overview:tabs.v3.home.unitBilled')}</div>
        </div>
        <div className="tc-data-card tc-ov-v3-metric">
          <div className="tc-ov-v3-metric-head">
            <VideoCameraOutlined /> {t('overview:tabs.v3.home.videos')}
          </div>
          <div className="tc-ov-v3-metric-value">{usage?.videos.used ?? '—'}</div>
          <div className="tc-ov-v3-metric-sub">{t('overview:tabs.v3.home.unitBilled')}</div>
        </div>
        <div className="tc-data-card tc-ov-v3-metric">
          <div className="tc-ov-v3-metric-head">
            <MessageOutlined /> {t('overview:tabs.v3.home.aiChat')}
          </div>
          <div className="tc-ov-v3-metric-value">{usage?.text.used ?? '—'}</div>
          <div className="tc-ov-v3-metric-sub">{t('overview:tabs.v3.home.unitBilled')}</div>
        </div>
      </div>

      <div className="tc-ov-v3-charts-row">
        <div className="tc-data-card tc-ov-v3-chart-wide" data-testid="overview-usage-trend">
          <div className="tc-ov-panel-head">
            <span className="tc-ov-panel-title">{t('overview:tabs.v3.home.trendTitle')}</span>
            <Segmented
              size="small"
              value={days}
              onChange={(v) => setDays(v as 7 | 14 | 30)}
              options={[
                { label: t('overview:tabs.v3.home.days', { n: 7 }), value: 7 },
                { label: t('overview:tabs.v3.home.days', { n: 14 }), value: 14 },
                { label: t('overview:tabs.v3.home.days', { n: 30 }), value: 30 },
              ]}
            />
          </div>
          {trendError ? (
            <Alert type="error" showIcon message={trendError} />
          ) : trendLoading ? (
            <Skeleton active paragraph={{ rows: 4 }} />
          ) : (
            <TrendLineChart
              points={trendPoints}
              formatValue={(v) => String(v)}
              formatDate={(d) => d.slice(5)}
            />
          )}
        </div>
        <div className="tc-data-card tc-ov-v3-chart-side" data-testid="overview-usage-distribution">
          <div className="tc-ov-panel-head">
            <span className="tc-ov-panel-title">{t('overview:tabs.v3.home.distTitle')}</span>
          </div>
          {distribution.length === 0 ? (
            <Empty description={t('overview:tabs.v3.home.distEmpty')} image={Empty.PRESENTED_IMAGE_SIMPLE} />
          ) : (
            <div className="tc-ov-v3-dist-list">
              {distribution.map((item) => {
                const pct = distTotal > 0 ? Math.round((item.count / distTotal) * 1000) / 10 : 0
                const label = t(`overview:tabs.v3.home.cat.${item.key}`, { defaultValue: item.key })
                return (
                  <div key={item.key} className="tc-ov-v3-dist-row">
                    <span>{label}</span>
                    <span>
                      {item.count} · {pct}%
                    </span>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
